import { Component, ChangeDetectionStrategy, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PaperIconComponent, PaperButtonComponent } from '@ricoh-paper/angular';
import { MiniEditorComponent } from '../../components/mini-editor/mini-editor.component';
import { FeedbackService, type FeedbackInput } from '../../../core/services/feedback.service';
import { FeedbackUiService } from '../../../core/services/feedback-ui.service';
import { ReleasesService } from '../../../core/services/releases.service';
import { ShellStateService } from '../../../core/services/shell-state.service';
import { AnalyticsService } from '../../../core/services/analytics.service';
import { ANALYTICS_EVENTS } from '../../../core/analytics/events';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { LocaleService } from '../../../core/services/locale.service';

type Category = FeedbackInput['category'];
type Mood = 'negative' | 'neutral' | 'positive';

/**
 * `paper-feedback-widget` — the beta feedback panel, mounted once in the app
 * shell and opened through `FeedbackUiService`: from the profile menu's Send
 * feedback, the settings rail and the desktop app's Help menu.
 *
 * 4 Oct 2026: the floating Feedback button that opened it from the bottom-left
 * is gone (Nath: "it gets in the way"). The panel opens where the profile menu
 * was, under the avatar, and closes with its own close button or Escape. The
 * flow, the copy and FEEDBACK_SUBMITTED are unchanged; the What's New dot the
 * button carried sits beside Send feedback in the profile menu now. Still
 * desktop only, as the button was.
 */
@Component({
  selector: 'paper-feedback-widget',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, PaperIconComponent, PaperButtonComponent, MiniEditorComponent, TranslocoPipe],
  host: { '(document:keydown.escape)': 'onEscape()' },
  template: `
    @if (isOpen()) {
      <div class="fb-panel" role="dialog" data-companion-avoid data-testid="feedback-panel"
           [attr.aria-label]="'shared.feedback.send_feedback' | transloco">

        @if (submitted()) {
          <div class="fb-success">
            <div class="fb-success-icon"><paper-icon name="check" [size]="24" /></div>
            <span class="fb-success-title">{{ 'shared.feedback.thank_you' | transloco }}</span>
            <span class="fb-success-text">{{ 'shared.feedback.feedback_submitted_will_be_analysed' | transloco }}</span>
            <paper-button intent="secondary" size="sm" (click)="close()">{{ 'shared.feedback.close' | transloco }}</paper-button>
            <button class="fb-history-link fb-history-link--success" (click)="viewMyFeedback()">
              <paper-icon name="list" [size]="12" />
              {{ 'shared.feedback.view_all_my_feedback' | transloco }}
            </button>
          </div>
        } @else {

          <div class="fb-header">
            <div class="fb-header-row">
              <span class="fb-title">{{ 'shared.feedback.send_feedback_title' | transloco }}</span>
              <button class="fb-history-link" (click)="viewMyFeedback()">{{ 'shared.feedback.view_history' | transloco }}</button>
              <paper-button class="fb-close" intent="ghost" size="xs" icon="close" shape="icon"
                [ariaLabel]="'shared.feedback.close' | transloco" (click)="close()" />
            </div>
            <span class="fb-subtitle">{{ 'shared.feedback.help_us_improve_ricoh_workplace' | transloco }}</span>
          </div>

          <!-- Always-on entry point to the What's New panel. Unread state
               carries a pulsing brand dot + count; read state collapses to
               a quieter "What's new" pill so the panel is always reachable
               from here even after you've caught up. -->
          <button class="fb-whats-new-cta"
                  [class.is-read]="!releases.hasUnread()"
                  (click)="openWhatsNew()"
                  [attr.aria-label]="whatsNewAriaLabel()">
            @if (releases.hasUnread()) {
              <span class="fb-whats-new-dot" aria-hidden="true"></span>
              <span class="fb-whats-new-text">{{ whatsNewUnreadLabel() }}</span>
            } @else {
              <paper-icon name="sparkles" [size]="12" aria-hidden="true" />
              <span class="fb-whats-new-text">{{ 'shared.feedback.whats_new_release_notes' | transloco }}</span>
            }
            <paper-icon name="arrow-right" [size]="11" aria-hidden="true" />
          </button>

          <div class="fb-categories" role="radiogroup" [attr.aria-label]="'shared.feedback.feedback_type' | transloco">
            @for (cat of categoryItems(); track cat.value) {
              <button class="fb-cat" [class.active]="category() === cat.value"
                      role="radio" [attr.aria-checked]="category() === cat.value"
                      (click)="category.set(cat.value)">
                <paper-icon [name]="cat.icon" [size]="12" />
                {{ cat.label }}
              </button>
            }
          </div>

          <div class="fb-editor-wrap">
            <im-mini-editor
              [content]="message()"
              [placeholder]="'shared.feedback.describe_issue_suggestion_feedback' | transloco"
              (contentChange)="message.set($event)" />
          </div>

          <!-- Screenshot area -->
          <div class="fb-screenshot-area">
            @if (screenshot()) {
              <div class="fb-screenshot-preview">
                <img [src]="screenshot()" [alt]="'shared.feedback.screenshot' | transloco" class="fb-screenshot-img" />
                <button class="fb-screenshot-remove" (click)="screenshot.set(null)" [attr.aria-label]="'shared.feedback.remove_screenshot' | transloco">
                  <paper-icon name="close" [size]="10" />
                </button>
              </div>
            } @else {
              <div class="fb-screenshot-actions">
                <button class="fb-attach-btn" (click)="triggerScreenshotUpload()">
                  <paper-icon name="photo" [size]="12" />
                  {{ 'shared.feedback.attach_screenshot' | transloco }}
                </button>
                <button class="fb-attach-btn" (click)="pasteFromClipboard()">
                  <paper-icon name="file-text" [size]="12" />
                  {{ 'shared.feedback.paste' | transloco }}
                </button>
              </div>
              <input class="fb-file-input" type="file" accept="image/*"
                     [attr.aria-label]="'shared.feedback.attach_a_screenshot' | transloco"
                     (change)="onScreenshotSelect($event)"
                     #screenshotInput />
            }
          </div>

          <div class="fb-mood" role="radiogroup" [attr.aria-label]="'shared.feedback.how_are_you_feeling' | transloco">
            <span class="fb-mood-label">{{ 'shared.feedback.how_are_you_feeling_label' | transloco }}</span>
            <div class="fb-mood-options">
              @for (m of moodItems(); track m.value) {
                <button class="fb-mood-btn" [class.active]="mood() === m.value"
                        role="radio" [attr.aria-checked]="mood() === m.value"
                        [title]="m.label"
                        (click)="mood.set(mood() === m.value ? null : m.value)">
                  <paper-icon [name]="m.icon" [size]="16" />
                </button>
              }
            </div>
          </div>

          <paper-button intent="primary" size="md" [block]="true"
                     [disabled]="!hasContent() || feedbackService.submitting()"
                     [loading]="feedbackService.submitting()"
                     (click)="onSubmit()">
            {{ (feedbackService.submitting() ? 'shared.feedback.submitting' : 'shared.feedback.submit_feedback') | transloco }}
          </paper-button>

          @if (error()) {
            <span class="fb-error">{{ error() }}</span>
          }
        }
      </div>
    }
  `,
  styles: `
    /* Where the profile menu was: Send feedback replaces it, under the avatar. */
    :host { position: fixed; top: calc(var(--nav-h, 48px) + 10px); right: 18px; z-index: 95; }

    @keyframes fb-trigger-pulse {
      0%, 100% {
        box-shadow: 0 0 0 2px var(--card-bg),
                    0 0 0 0 color-mix(in srgb, var(--brand-color, var(--color-primary)) 60%, transparent);
      }
      50% {
        box-shadow: 0 0 0 2px var(--card-bg),
                    0 0 0 6px color-mix(in srgb, var(--brand-color, var(--color-primary)) 0%, transparent);
      }
    }

    /* What's new CTA inside the open feedback panel — sits between
       the header and the category radio group. Brand-tinted pill
       so it visually links back to the trigger dot. */
    .fb-whats-new-cta {
      display: flex; align-items: center; gap: 8px;
      width: 100%;
      margin: 0 0 12px;
      padding: 9px 12px;
      font-family: inherit; font-size: 12px; font-weight: 500;
      color: var(--text);
      background: color-mix(in srgb, var(--brand-color, var(--color-primary)) 10%, transparent);
      border: 1px solid color-mix(in srgb, var(--brand-color, var(--color-primary)) 28%, transparent);
      border-radius: 8px;
      cursor: pointer;
      text-align: left;
      transition: background 120ms ease, border-color 120ms ease;

      &:hover {
        background: color-mix(in srgb, var(--brand-color, var(--color-primary)) 16%, transparent);
        border-color: color-mix(in srgb, var(--brand-color, var(--color-primary)) 45%, transparent);
      }

      paper-icon {
        margin-left: auto;
        color: var(--brand-color, var(--color-primary));
        flex-shrink: 0;
      }

      /* Caught-up state — quieter than the unread pill so it reads as an
         always-available entry point, not a notification. The leading
         sparkles icon keeps its size; the trailing arrow stays for affordance. */
      &.is-read {
        background: var(--surface-2);
        border-color: var(--border-low);
        color: var(--text-secondary);
        font-weight: 400;

        &:hover {
          background: var(--surface-3);
          border-color: var(--border-med);
          color: var(--text);
        }

        paper-icon {
          color: var(--text-tertiary);
        }

        /* Leading sparkles icon sits inline (default order), not pushed right. */
        paper-icon:first-child {
          margin-left: 0;
        }
      }
    }

    .fb-whats-new-dot {
      width: 8px; height: 8px;
      border-radius: 50%;
      background: var(--brand-color, var(--color-primary));
      flex-shrink: 0;
      animation: fb-trigger-pulse 1.8s ease-in-out infinite;
    }

    .fb-whats-new-text { flex: 1; line-height: 1.4; }

    .fb-panel {
      position: relative;
      width: min(380px, calc(100vw - 36px));
      background: var(--card-bg-solid);
      border: 1px solid var(--border-low);
      border-radius: 16px;
      box-shadow: var(--shadow-lg, var(--panel-shadow));

      :host-context([data-theme="light"]) & {
        box-shadow: var(--shadow-lg, var(--panel-shadow));
      }
      padding: 20px;
      animation: fb-drop-in 0.25s cubic-bezier(0.16, 1, 0.3, 1);
      max-height: calc(100vh - var(--nav-h, 48px) - 26px);
      overflow-y: auto;
    }

    @keyframes fb-drop-in {
      from { opacity: 0; transform: translateY(-6px); }
      to { opacity: 1; transform: translateY(0); }
    }

    .fb-close { margin-inline-start: 4px; }

    .fb-header { margin-bottom: 14px; }
    .fb-header-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
    .fb-header-row .fb-title { flex: 1; }
    .fb-title { font-size: 16px; font-weight: 600; color: var(--text); display: block; }
    .fb-subtitle { font-size: 12px; color: var(--text-tertiary); }

    .fb-history-link {
      display: inline-flex; align-items: center; gap: 4px;
      background: none; border: none; padding: 0;
      font-family: inherit; font-size: 12px;
      color: var(--color-primary); cursor: pointer;
      &:hover { text-decoration: underline; }
      &--success { margin-top: 4px; }
    }

    .fb-categories {
      display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 12px;
    }

    .fb-cat {
      display: flex; align-items: center; gap: 4px;
      padding: 5px 10px; border-radius: 100px;
      border: 1px solid var(--border-low);
      background: transparent; color: var(--text-secondary);
      font-family: inherit; font-size: 11px; font-weight: 500;
      cursor: pointer; transition: all 0.15s;
      &:hover { border-color: var(--border-med); color: var(--text); }
      &.active { background: var(--color-primary-bg); border-color: var(--color-primary); color: var(--color-primary); }
      &:focus-visible { outline: 2px solid var(--color-primary); outline-offset: 1px; }
    }

    .fb-editor-wrap {
      margin-bottom: 10px;
      border: 1px solid var(--border-low);
      border-radius: 10px;
      overflow: hidden;

      im-mini-editor {
        --editor-min-height: 100px;
      }
    }

    /* Screenshot */
    .fb-screenshot-area { margin-bottom: 10px; }

    .fb-file-input { display: none; }

    .fb-screenshot-actions {
      display: flex; gap: 6px;
    }

    .fb-attach-btn {
      display: flex; align-items: center; gap: 4px;
      padding: 5px 10px; border-radius: 6px;
      border: 1px dashed var(--border-low);
      background: transparent; color: var(--text-tertiary);
      font-family: inherit; font-size: 11px; font-weight: 500;
      cursor: pointer; transition: all 0.15s;
      &:hover { border-color: var(--border-med); color: var(--text-secondary); border-style: solid; }
      &:focus-visible { outline: 2px solid var(--color-primary); outline-offset: 1px; }
    }

    .fb-screenshot-preview {
      position: relative;
      border-radius: 8px; overflow: hidden;
      border: 1px solid var(--border-low);
    }

    .fb-screenshot-img {
      width: 100%; max-height: 160px; object-fit: cover; display: block;
    }

    .fb-screenshot-remove {
      position: absolute; top: 6px; right: 6px;
      width: 22px; height: 22px;
      display: flex; align-items: center; justify-content: center;
      border-radius: 50%; border: none;
      background: color-mix(in srgb, var(--surface-base, var(--bg)) 70%, transparent);
      color: var(--brand-on-primary, var(--text-on-accent));
      cursor: pointer;
      &:hover { background: color-mix(in srgb, var(--surface-base, var(--bg)) 86%, transparent); }
    }

    /* Mood */
    .fb-mood {
      display: flex; align-items: center; justify-content: space-between;
      margin-bottom: 14px;
    }
    .fb-mood-label { font-size: 12px; color: var(--text-tertiary); }
    .fb-mood-options { display: flex; gap: 4px; }
    .fb-mood-btn {
      width: 36px; height: 36px;
      display: flex; align-items: center; justify-content: center;
      border: 1px solid var(--border-low); border-radius: 8px;
      background: transparent; color: var(--text-tertiary);
      cursor: pointer; transition: all 0.15s;
      &:hover { border-color: var(--border-med); color: var(--text-secondary); }
      &.active { border-color: var(--color-primary); background: var(--color-primary-bg); color: var(--color-primary); }
      &:focus-visible { outline: 2px solid var(--color-primary); outline-offset: 1px; }
    }

    .fb-submit {
      width: 100%; padding: 10px;
      background: var(--accent, var(--color-primary)); color: var(--brand-on-primary, var(--text-on-accent));
      border: none; border-radius: 8px;
      font-family: inherit; font-size: 13px; font-weight: 600;
      cursor: pointer; transition: filter 0.15s;
      &:hover { filter: brightness(1.1); }
      &:disabled { opacity: 0.5; cursor: not-allowed; filter: none; }
      &:focus-visible { outline: 2px solid var(--color-primary); outline-offset: 2px; }
    }

    .fb-error { display: block; margin-top: 8px; font-size: 12px; color: var(--error-strong, var(--danger)); }

    /* Success */
    .fb-success {
      display: flex; flex-direction: column;
      align-items: center; gap: 8px;
      padding: 24px 0; text-align: center;
    }
    .fb-success-icon {
      width: 48px; height: 48px;
      display: flex; align-items: center; justify-content: center;
      border-radius: 50%;
      background: var(--success-soft);
      color: var(--success-strong, var(--success-text));
    }
    .fb-success-title { font-size: 16px; font-weight: 600; color: var(--text); }
    .fb-success-text { font-size: 13px; color: var(--text-secondary); max-width: 260px; }
    .fb-close-btn {
      margin-top: 8px; padding: 8px 20px;
      background: var(--surface-3); border: none; border-radius: 8px;
      color: var(--text); font-family: inherit; font-size: 13px; font-weight: 500;
      cursor: pointer;
      &:hover { background: var(--surface-hover); }
    }

    /* Desktop only, as the floating button was: the profile menu offers
       Send feedback above 768px, and so does this panel. */
    @media (max-width: 768px) {
      :host { display: none; }
    }

    @media (prefers-reduced-motion: reduce) {
      .fb-panel { animation: none; }
    }
  `,
})
export class PaperFeedbackWidgetComponent {
  private readonly router = inject(Router);
  private readonly ui = inject(FeedbackUiService);
  protected readonly feedbackService = inject(FeedbackService);
  protected readonly releases = inject(ReleasesService);
  private readonly shellState = inject(ShellStateService);
  private readonly analytics = inject(AnalyticsService);
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  /** Localised aria-label for the What's New CTA inside the open panel. */
  protected readonly whatsNewAriaLabel = computed<string>(() => {
    this.locale.locale();
    if (this.releases.hasUnread()) {
      return this.transloco.translate('shared.feedback.whats_new_aria_with_updates', { count: this.releases.unreadCount() });
    }
    return this.transloco.translate('shared.feedback.whats_new_aria_caught_up');
  });

  /** Localised "N new updates since you last looked" text. */
  protected readonly whatsNewUnreadLabel = computed<string>(() => {
    this.locale.locale();
    const count = this.releases.unreadCount();
    const key = count === 1 ? 'shared.feedback.new_updates_since_one' : 'shared.feedback.new_updates_since_other';
    return this.transloco.translate(key, { count });
  });

  /** Localised feedback category buttons. */
  protected readonly categoryItems = computed(() => {
    this.locale.locale();
    return [
      { value: 'bug' as Category, label: this.transloco.translate('shared.feedback.category_bug'), icon: 'alert-circle' },
      { value: 'suggestion' as Category, label: this.transloco.translate('shared.feedback.category_suggestion'), icon: 'zap' },
      { value: 'question' as Category, label: this.transloco.translate('shared.feedback.category_question'), icon: 'help-circle' },
      { value: 'praise' as Category, label: this.transloco.translate('shared.feedback.category_praise'), icon: 'star' },
    ];
  });

  /** Localised mood buttons. */
  protected readonly moodItems = computed(() => {
    this.locale.locale();
    return [
      { value: 'negative' as Mood, label: this.transloco.translate('shared.feedback.mood_not_great'), icon: 'thumbs-down' },
      { value: 'neutral' as Mood, label: this.transloco.translate('shared.feedback.mood_okay'), icon: 'minus' },
      { value: 'positive' as Mood, label: this.transloco.translate('shared.feedback.mood_great'), icon: 'thumbs-up' },
    ];
  });

  /** Open state lives in a shared service so that triggers outside this
   *  component (e.g. the settings rail) can toggle the panel. */
  protected readonly isOpen = this.ui.isOpen;

  protected readonly submitted = signal(false);
  protected readonly category = signal<Category>('suggestion');
  protected readonly message = signal('');
  protected readonly mood = signal<Mood | null>(null);
  protected readonly screenshot = signal<string | null>(null);
  protected readonly error = signal<string | null>(null);

  protected readonly categories = [
    { value: 'bug' as Category, label: 'Bug', icon: 'alert-circle' },
    { value: 'suggestion' as Category, label: 'Suggestion', icon: 'zap' },
    { value: 'question' as Category, label: 'Question', icon: 'help-circle' },
    { value: 'praise' as Category, label: 'Praise', icon: 'star' },
  ];

  protected readonly moods = [
    { value: 'negative' as Mood, label: 'Not great', icon: 'thumbs-down' },
    { value: 'neutral' as Mood, label: 'Okay', icon: 'minus' },
    { value: 'positive' as Mood, label: 'Great', icon: 'thumbs-up' },
  ];

  protected hasContent(): boolean {
    const msg = this.message().replace(/<[^>]*>/g, '').trim();
    return msg.length > 0;
  }

  protected viewMyFeedback(): void {
    this.close();
    this.router.navigateByUrl('/profile/my-feedback');
  }

  /** Close the feedback panel and open the dedicated What's New
   *  slide-in. The slide-in's own auto-mark-on-view logic will clear
   *  the unread state, which in turn hides the trigger dot. The
   *  PANEL_OPENED analytics event is fired by the panel itself
   *  (single point of truth) using the source we stamp here. */
  protected openWhatsNew(): void {
    this.close();
    this.shellState.openWhatsNew('feedback-cta');
  }

  /** Escape closes the panel, as its close button does. */
  protected onEscape(): void {
    if (this.isOpen()) this.close();
  }

  protected close(): void {
    this.ui.close();
    this.resetForm();
    // Opened again from the profile menu, it starts at the form, not at the last thank-you.
    this.submitted.set(false);
  }

  protected async onSubmit(): Promise<void> {
    if (!this.hasContent()) return;
    this.error.set(null);

    // Snapshot before the await: the panel stays interactive while the request
    // is in flight, so reading the signals again afterwards would report
    // whatever the user has since typed rather than what was actually sent.
    const category = this.category();
    const mood = this.mood();
    const screenshot = this.screenshot();

    const success = await this.feedbackService.submit({
      category,
      message: this.message().trim(),
      mood: mood ?? undefined,
      screenshot: screenshot ?? undefined,
    });

    if (success) {
      this.analytics.capture(ANALYTICS_EVENTS.FEEDBACK_SUBMITTED, {
        category,
        mood_selected: mood !== null,
        has_screenshot: screenshot !== null,
      });
      this.submitted.set(true);
      this.resetForm();
    } else {
      this.error.set(this.transloco.translate('shared.feedback.failed_to_submit_try_again'));
    }
  }

  protected triggerScreenshotUpload(): void {
    const input = document.querySelector<HTMLInputElement>('.fb-panel input[type="file"]');
    input?.click();
  }

  protected onScreenshotSelect(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file?.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => this.screenshot.set(reader.result as string);
    reader.readAsDataURL(file);
  }

  protected async pasteFromClipboard(): Promise<void> {
    try {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        const imageType = item.types.find(t => t.startsWith('image/'));
        if (imageType) {
          const blob = await item.getType(imageType);
          const reader = new FileReader();
          reader.onload = () => this.screenshot.set(reader.result as string);
          reader.readAsDataURL(blob);
          return;
        }
      }
    } catch {
      this.error.set(this.transloco.translate('shared.feedback.clipboard_read_failed'));
      setTimeout(() => this.error.set(null), 3000);
    }
  }

  private resetForm(): void {
    this.category.set('suggestion');
    this.message.set('');
    this.mood.set(null);
    this.screenshot.set(null);
    this.error.set(null);
  }
}
